import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { LockIcon, PinIcon } from '@seedlang/icons';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { isBlank, isPresent } from '@seedlang/utils';
import cx from 'classnames';
import ExerciseProgress from 'components/exercise/exercise_progress';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import WorksheetLabel, { LabelWrapper, Label } from 'components/worksheet/worksheet_label';
import GridItemTextPreview from 'components/worksheet/grid_item_text_preview';
import Tags from 'components/worksheet/tags';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Link from 'components/link';
import { YoutubeThumbnail } from 'components/worksheet/youtube_thumbnail_player';
import PodcastEpisodeThumbnail from 'components/worksheet/podcast_episode_thumbnail';

const Wrapper = styled.div`
  background: white;
  border-radius: ${props => props.borderRadius};
  position: relative;
`;

const ContentWrapper = styled.div`
  padding: 20px 20px 15px 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Name = styled.div`
  text-align: left;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
`;

const EmptyName = styled.div`
  width: 100%;
  border-radius: 10px;
  background: linear-gradient(-134deg, rgb(216 216 216) 30%, rgb(239 239 239) 100%);
  height: 30px;
  opacity: 0.6;
`;


const Thumbnail = styled.div`
  width: 100%;
  height: fit-content;
  min-height: ${props => props.minHeight};
  background: linear-gradient(-134deg, rgb(216 216 216) 30%, rgb(239 239 239) 100%);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  > img {
    width: 100%;
    border-radius: 10px;
    height: 100%;
    cursor: pointer;
  }
  .grayscale {
    filter: gray;
    filter: grayscale(1);
    transition: filter 0.3s ease;
    &:hover {
      filter: none;
    }
  }
  .pin-icon {
    position: absolute;
    height: 14px;
    width: 14px;
    padding: 5px;
    opacity: 0.7;
    right: 33px;
    top: 22px;
    svg {
      width: 14px;
      fill: #FFF;
    }
  }
`;

const CornerIconWrapper = styled.div`
  z-index: ${Theme.z['foreground']};
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: ${props => props.background};
  position: absolute;
  top: 5px;
  right: 5px;
  pointer-events: none;
  .icon-wrapper {
    svg {
      fill: #FFF;
      width: ${props => props.iconWidth};
      position: absolute;
      left: ${props => props.left};
      top: ${props => props.top};
    }
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0 5px 0;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 10px;
  img {
    width: 40px;
    height: 40px;
    border-radius: ${props => props.borderRadius};
  }
`;

const YoutubeIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background: #ed2021;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 24px;
    height: 28px;
  }
`;

const Details = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ClickableContainer = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const ProgressWrapper = styled.div`
  height: 15px;
  border-radius: ${props => props.borderRadius};
  overflow: hidden;
  display: flex;
  flex-direction: column-reverse;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

@observer
class WorksheetGridItem extends React.Component {

  @computed get showFreeLabel() {
    return this.props.worksheet?.published && this.props.worksheet?.accessWithoutMembership && (!ExerciseUI.user.isMember || AppUI.isPaymentMigrationStartable);
  }

  @computed get showTags() {
    const isHomepage = (AppUI.userIsSignedIn && AppUI.routeStore.routeName === AppUI.site.signedInHomePageRoute) || (AppUI.userIsSignedOut && AppUI.routeStore.routeName === AppUI.site.signedOutHomePageRoute);
    return !isHomepage;
  }

  @computed get accessStatus() {
    return this.props.worksheet?.accessStatus;
  }

  @computed get showLockIcon() {
    return this.accessStatus && !this.accessStatus.hasAccess && !this.showFreeLabel;
  }

  @computed get showPinIcon() {
    return !this.props.hidePinIcon && this.accessStatus && this.accessStatus.hasAccess && this.props.worksheet?.pinned;
  }

  @computed get showYoutubeThumbnail() {
    return isPresent(this.props.worksheet?.formattedYoutubeUrl);
  }

  @computed get podcastImageUrl() {
    return this.props.worksheet?.podcastEpisode?.imageUrl || this.props.worksheet?.podcastEpisode?.podcast?.imageUrl;
  }

  onClick = () => {
    ExerciseUI.set('worksheetId', this.props.worksheet.id);
    ExerciseUI.worksheetStore.setShowData(this.props.worksheet);
    this.props.onSelect?.(this.props.worksheet.id);
  }

  render() {
    if (!this.props.worksheet) {
      return null;
    }

    return (
      <Wrapper
        id={this.props.worksheet.id}
        borderRadius={ExerciseUI.layout.isMobile ? '0' : '10px'}
      >
        <ContentWrapper>
          <ClickableContainer
            to={{
              name: `${ExerciseUI.worksheetNamespace}.show`,
              params: {
                worksheetId: this.props.worksheet.routeId,
              },
            }}
            onClick={this.onClick}
          >
            {
              isPresent(this.props.worksheet) && !this.props.worksheet.published &&
                <LabelWrapper>
                  <Label
                    background='#b48181'
                    width='85px'
                  >
                    UNPUBLISHED
                  </Label>
                </LabelWrapper>
            }
            {
              this.props.worksheet?.published && this.props.worksheet?.isNew && !this.showFreeLabel &&
                <WorksheetLabel
                  type='new'
                  worksheet={this.props.worksheet}
                />
            }
            {
              this.showFreeLabel &&
                <WorksheetLabel
                  type='free'
                  worksheet={this.props.worksheet}
                />
            }
            {
              this.showLockIcon &&
                <CornerIconWrapper
                  background='#777777'
                  iconWidth='11px'
                  left='10px'
                  top='7px'
                >
                  <LockIcon />
                </CornerIconWrapper>
            }
            {
              this.showPinIcon &&
                <CornerIconWrapper
                  background={AppUI.site.secondaryAccentColor || '#777777'}
                  iconWidth='14px'
                  left='8px'
                  top='8px'
                >
                  <PinIcon />
                </CornerIconWrapper>
            }
            {
              isPresent(this.props.worksheet?.podcastEpisode) &&
                <Thumbnail>
                  <PodcastEpisodeThumbnail
                    className={cx({grayscale: this.showLockIcon})}
                    worksheet={this.props.worksheet}
                  />
                </Thumbnail>
            }
            {
              isBlank(this.props.worksheet?.podcastEpisode) && isPresent(this.props.worksheet?.previewText) && isBlank(this.props.worksheet?.imageUrl) &&
                <GridItemTextPreview
                  worksheet={this.props.worksheet}
                />
            }
              {
                isBlank(this.props.worksheet?.podcastEpisode) && (isBlank(this.props.worksheet?.previewText) || isPresent(this.props.worksheet?.imageUrl)) &&
                <Thumbnail>
                  {
                    isPresent(this.props.worksheet?.imageUrl) && this.showYoutubeThumbnail &&
                      <YoutubeThumbnail
                        thumbnailUrl={this.props.worksheet?.imageUrl}
                        imageClassName={cx({grayscale: this.showLockIcon})}
                        durationSeconds={this.props.worksheet?.videoDurationSeconds}
                        showPlayButton={false}
                      />
                  }
                  {
                    isPresent(this.props.worksheet?.imageUrl) && !this.showYoutubeThumbnail &&
                    <img
                      alt=''
                      src={this.props.worksheet?.imageUrl}
                      className={cx({grayscale: this.showLockIcon})}
                    />
                  }
                </Thumbnail>
              }
            <NameRow>
              <IconWrapper
                borderRadius={isPresent(this.podcastImageUrl) ? '5px' : '40px'}
              >
                {
                  isPresent(this.podcastImageUrl) &&
                    <img
                      alt=''
                      src={this.podcastImageUrl}
                    />
                }
                {
                  isBlank(this.props.worksheet?.podcastEpisode) &&
                    <YoutubeIconWrapper>
                      <img
                        alt=''
                        src='/images/youtube_icon_inverse.png'
                      />
                    </YoutubeIconWrapper>
                }
              </IconWrapper>
              {
                isPresent(this.props.worksheet?.name) &&
                  <Name>
                    {this.props.worksheet?.name}
                  </Name>
              }
              {
                isBlank(this.props.worksheet?.name) &&
                <EmptyName />
              }
            </NameRow>
          </ClickableContainer>
          {
            isPresent(this.props.worksheet) && this.showTags &&
              <Details>
                <Tags
                  episodeCode={this.props.worksheet.episodeCode}
                  worksheet={this.props.worksheet}
                  onUpdateFilter={this.props.onUpdateFilter}
                  gridLayout={this.props.gridLayout}
                />
              </Details>
          }
        </ContentWrapper>
        <ProgressWrapper
          borderRadius={ExerciseUI.layout.isMobile ? '0' : '10px'}
        >
          <ExerciseProgress
            hideCounts
            current={this.props.worksheet.progressPercentage}
            total={100}
            borderTopRightRadius='5px'
            borderTopLeftRadius={ExerciseUI.layout.isMobile ? '0' : '10px'}
            borderBottomRightRadius='5px'
            borderBottomLeftRadius='0px'
          />
        </ProgressWrapper>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(WorksheetGridItem);
